import React from 'react';
import BaseRadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { isMobile } from 'react-device-detect';
import Radio from '../checkboxes/Radio';

const useStyles = makeStyles((theme) =>
  createStyles({
    common: {
      marginLeft: 0,
      marginRight: 0,
      backgroundColor: theme.palette.common.white,
      marginTop: '7px !important',
      marginBottom: '7px !important',
    },
    icon: {
      paddingLeft: 2,
      paddingTop: 7,
      paddingBottom: 7,
      paddingRight: 8,
    },
    root: {
      height: 31,
    },
    rootBordered: {
      borderRadius: 3,
      borderColor: theme.palette.grey.A100,
      borderWidth: 1,
      borderStyle: 'solid',
    },
    label: {
      color: theme.palette.text.primary,
      fontSize: 14,
      padding: 3,
      lineHeight: 1.5,
    },
    labelRadioGroupPaddingMobile: { padding: 3 },
    labelPaddingDesktop: { padding: 8 },
    circleDesktop: {
      width: 21,
      height: 21,
    },
    circleMobile: {
      width: 17,
      height: 17,
    },
    marginRadio: {
      margin: '4px 6px 0 0',
    },
    smallBlock: {
      width: '32%',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      [theme.breakpoints.up('md')]: {
        width: '32%',
      },
    },
    largeBlock: {
      width: '49%',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      [theme.breakpoints.up('sm')]: {
        width: '49%',
      },
    },
    blockDesktop: {
      height: 60,
      '&:hover': {
        border: '2px solid #18A184',
      },
    },
    blockMobile: {
      height: '31px !important',
      width: '100% !important',
    },
    selected: { borderColor: theme.palette.secondary.main },
    error: {
      borderColor: 'red !important',
    },
    threeChilds: {
      '& label:first-child': {
        marginRight: '2%',
        [theme.breakpoints.down('sm')]: {
          marginRight: 0,
        },
      },
      '& label:last-child': {
        marginLeft: '2%',
        [theme.breakpoints.down('sm')]: {
          marginLeft: 0,
        },
      },
    },
    twoChilds: {
      '& label:first-child': {
        marginRight: '1%',
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
        },
      },
      '& label:last-child': {
        marginLeft: '1%',
        [theme.breakpoints.down('xs')]: {
          marginLeft: 0,
        },
      },
    },
  }),
);

const getRadioGroupClass = (items: any, classes: any) => {
  if (!isMobile) {
    return items.length > 2 ? classes.threeChilds : classes.twoChilds;
  }
  return '';
};

interface Props {
  name?: any;
  value?: any;
  onChange?: any;
  items?: any;
  isBordered?: any;
  isError?: any;
  'aria-label'?: any;
  inputRef?: React.Ref<any>;
}

const RadioGroupHorizontal: React.FC<Props> = ({
  name,
  value,
  onChange,
  items,
  isBordered,
  isError,
  'aria-label': ariaLabel,
  inputRef,
}) => {
  const classes = useStyles();
  const radioGroupClass = getRadioGroupClass(items, classes);
  return (
    <BaseRadioGroup
      row
      name={name}
      value={value}
      onChange={onChange}
      className={radioGroupClass}
      aria-label={ariaLabel || name}
      ref={inputRef}
    >
      {items.map((item: any) => (
        <FormControlLabel
          value={item.value}
          label={item.label}
          disabled={item.disabled}
          control={
            <Radio
              className={classes.icon}
              classes={{
                circle: isMobile ? classes.circleMobile : classes.circleDesktop,
                customMargin: classes.marginRadio,
              }}
              checked={value === item.value}
              onClick={onChange}
              value={item.value}
            />
          }
          key={item.id || item.value}
          classes={{
            label: classes.label,
            root: isBordered ? classes.rootBordered : classes.root,
          }}
          className={[
            classes.common,
            item.value === value ? classes.selected : null,
            isMobile ? classes.labelRadioGroupPaddingMobile : classes.labelPaddingDesktop,
            items.length > 2 ? classes.smallBlock : classes.largeBlock,
            isMobile ? classes.blockMobile : classes.blockDesktop,
            isError ? classes.error : '',
          ].join(' ')}
        />
      ))}
    </BaseRadioGroup>
  );
};

export default RadioGroupHorizontal;
